import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktButton } from './marketplaceUi';
import SimulationResultsContent from '../simulationResults/SimulationResultsContent';
import { getStudyResults, type StudyResults } from './marketplaceApi';

/**
 * Results page the emailed link points to. Renders the study's simulation
 * output. v1 shows the structured response; richer charts are future work.
 */
const MarketplaceResults: React.FC = () => {
  const navigate = useNavigate();
  const { studyId } = useParams();
  const id = Number(studyId);
  const [data, setData] = useState<StudyResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    const poll = async () => {
      try {
        const r = await getStudyResults(id);
        if (active) setData(r);
      } catch (e) { if (active) setError(e instanceof Error ? e.message : 'Failed to load results'); }
    };
    poll();
    // Once we know the backing simulation id, SimulationResultsContent takes over
    // its own polling — stop the lightweight study poll.
    const t = setInterval(() => { if (!data?.ready && !data?.simulation_id) poll(); }, 8000);
    return () => { active = false; clearInterval(t); };
  }, [id, data?.ready, data?.simulation_id]);

  // Chat studies have no "still running" report — open the live chat instead.
  useEffect(() => {
    if (data?.use_case === 'chat') navigate(`/marketplace/studies/${id}/chat`, { replace: true });
  }, [data?.use_case, id, navigate]);

  if (error) return <MarketplaceShell maxWidth="max-w-3xl"><div className="text-red-600">{error}</div></MarketplaceShell>;
  if (!data) return <MarketplaceShell maxWidth="max-w-3xl"><div className="text-[13px] text-[#6b6b6b]">Loading…</div></MarketplaceShell>;

  const header = (
    <div className="mb-4 flex items-center justify-between">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#999]">{data.use_case} study</div>
        <h1 className="text-[22px] font-medium text-[#111]">Study results</h1>
      </div>
      <MktButton variant="secondary" onClick={() => navigate('/marketplace/run')}>Run another study</MktButton>
    </div>
  );

  // The platform's full results UI (handles its own running/completed states,
  // analysis panel, persona chat, export). Marketplace simulations are owned by
  // the same user, so its /simulations/{id} fetch resolves.
  if (data.simulation_id) {
    return (
      <MarketplaceShell maxWidth="max-w-7xl">
        {header}
        <SimulationResultsContent simulationId={data.simulation_id} onError={setError} marketplaceView />
      </MarketplaceShell>
    );
  }

  // Fallback (no backing simulation yet) — keep the lightweight running state.
  return (
    <MarketplaceShell maxWidth="max-w-4xl">
      {header}
      <div className="mt-6">
        <div className="flex flex-col items-center py-16 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <h2 className="mt-4 text-[15px] font-medium text-[#111]">Your study is still running</h2>
          <p className="mt-2 text-[13px] text-[#6b6b6b]">We’ll email you when it’s ready — this page updates automatically.</p>
        </div>
      </div>
    </MarketplaceShell>
  );
};

export default MarketplaceResults;
